import { useState, useMemo, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { message } from 'antd';

import { bookingJourneyType } from 'common/constants/booking';
import {
  CMS_QUERY_BOOKINGS_COUNT,
  CMS_QUERY_MANUAL_BOOKINGS,
  CMS_QUERY_BOOK_BOOKINGS,
} from './queries';

export const orderAll = 'ALL';

export const opportunityStages = {
  COMPLETED: 'COMPLETED',
  CANCELLATION: 'CANCELLATION',
  LOST: 'LOST',
};

const defaultOtherTableFilter = {
  pageNumber: 1,
  pageSize: 10,
};

const useTableFilters = ({ landlordId }) => {
  const [bookingType, setBookingType] = useState(bookingJourneyType.SEMI_AUTOMATIC);
  const [bookingStage, setBookingStage] = useState();
  const [otherTableFilter, setOtherTableFilter] = useState(defaultOtherTableFilter);
  const [tableData, setTableData] = useState();
  const [tableLoading, setTableLoading] = useState(false);
  const [refetchTableData, setRefetchTableData] = useState(null);

  const isHerculesBooking = useMemo(
    () => bookingType === bookingJourneyType.SEMI_AUTOMATIC,
    [bookingType],
  );

  const [queryBookingCount, { loading: queryBookingCountLoading, data: queryBookingCountData }] =
    useLazyQuery(CMS_QUERY_BOOKINGS_COUNT, {
      fetchPolicy: 'no-cache',
      variables: {
        slug: landlordId,
      },
      onCompleted: data => {
        const hasHerculesOrder = data?.landlord?.messagePropertyCount?.totalCount > 0;
        const hasManualOrder = data?.landlord?.manualCount?.totalCount > 0;

        if (!hasHerculesOrder && hasManualOrder) {
          setBookingType(bookingJourneyType.MANUAL);
        }
      },
    });

  const variables = useMemo(() => {
    const queryArguments = {
      slug: landlordId,
      bookingJourneys: [bookingType],
      stages: bookingStage ? [bookingStage] : null,
      ...otherTableFilter,
    };

    if (bookingType === bookingJourneyType.MANUAL) {
      queryArguments.states = ['IN_PROGRESS', 'COMPLETED', 'LOST'];
    }

    return queryArguments;
  }, [bookingStage, isHerculesBooking, otherTableFilter, bookingType]);

  const [
    queryLandlordBookings,
    { data: manualBookingData, loading: manualBookingLoading, refetch: refetchManualBooking },
  ] = useLazyQuery(CMS_QUERY_MANUAL_BOOKINGS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const [
    queryBookBooking,
    { data: bookBookingData, loading: bookBookingLoading, refetch: refetchBookBooking },
  ] = useLazyQuery(CMS_QUERY_BOOK_BOOKINGS, {
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
  });

  const changeBookingType = value => {
    setBookingType(value);
    setBookingStage();
    setOtherTableFilter(defaultOtherTableFilter);
  };

  const resetPageNumber = () => {
    setOtherTableFilter({ ...otherTableFilter, pageNumber: 1 });
  };

  const changeBookingStage = value => {
    if (value === orderAll) {
      setBookingStage();
    } else {
      setBookingStage(value);
    }
    resetPageNumber();
  };

  useEffect(() => {
    if (!queryBookingCountLoading) {
      if (
        queryBookingCountData?.landlord?.messagePropertyCount?.totalCount > 0 &&
        bookingType === bookingJourneyType.SEMI_AUTOMATIC
      ) {
        queryBookBooking({ variables });
      } else if (
        queryBookingCountData?.landlord?.manualCount?.totalCount > 0 &&
        bookingType === bookingJourneyType.MANUAL
      ) {
        queryLandlordBookings({ variables });
      }
    }
  }, [otherTableFilter, variables, queryBookingCountLoading]);

  useEffect(() => {
    queryBookingCount();
  }, []);

  useEffect(() => {
    setTableLoading(manualBookingLoading || bookBookingLoading);
  }, [manualBookingLoading, bookBookingLoading]);

  useEffect(() => {
    if (bookingType === bookingJourneyType.SEMI_AUTOMATIC) {
      setTableData(bookBookingData);
      setRefetchTableData(() => refetchBookBooking);
    } else if (bookingType === bookingJourneyType.MANUAL) {
      setTableData(manualBookingData);
      setRefetchTableData(() => refetchManualBooking);
    }
  }, [manualBookingData, bookBookingData, bookingType]);

  return {
    bookingType,
    setBookingType: changeBookingType,
    bookingStage,
    setBookingStage: changeBookingStage,
    otherTableFilter,
    setOtherTableFilter,
    queryBookingCountLoading,
    queryBookingCountData,
    tableData,
    tableLoading,
    refetchTableData,
  };
};

export default useTableFilters;
